import { HEIGHT, STAGE_LEFT, STAGE_TOP, WIDTH } from '~/lib/constants'
import { applyHomography, quadToQuad, rectQuad } from '~/lib/homography'
import { mapNodes, parsePath, pathString } from './path'
import { DEFAULT_FIT, OWN_FIT, surfaceQuad, surfaceToFrame } from './placement'

export const DEFAULT_OUTPUT = { width: 1920, height: 1080 }
export const ROLES = ['lit', 'cut']
export const FULL_FRAME = [
	[0, 0],
	[1, 0],
	[1, 1],
	[0, 1],
]
const MAX_OUTPUT = 8192

const isPlainObject = v => !!v && typeof v === 'object' && !Array.isArray(v)
const isQuad = q =>
	Array.isArray(q) && q.length === 4 && q.every(p => Array.isArray(p) && p.length === 2 && p.every(Number.isFinite))
const num = (v, fallback) => (Number.isFinite(v) ? v : fallback)
const size = (v, fallback) => (Number.isFinite(v) ? Math.min(MAX_OUTPUT, Math.max(16, Math.round(v))) : fallback)

const SLICE_COLORS = [
	[255, 212, 121],
	[102, 217, 239],
	[240, 128, 220],
	[140, 230, 120],
	[255, 150, 90],
	[170, 150, 255],
]

/** A slice's colour as `[r, g, b]` 0–255, its stored `color`, in the editor and on the test card alike. */
export const sliceColor = (data, id) => SLICE_COLORS[data.slices[id]?.color ?? 0]

/** The least used colour among `slices`, the first on a tie. */
export const nextSliceColor = slices => {
	const used = SLICE_COLORS.map(() => 0)
	for (const s of Object.values(slices)) if (used[s.color] !== undefined) used[s.color]++
	return used.indexOf(Math.min(...used))
}

export const newId = prefix => `${prefix}${Math.random().toString(36).slice(2, 9)}`

/** A `w`×`h` box fitted (contain) and centred in `W`×`H`, as a quad. */
export const fitQuad = (w, h, W, H) => {
	const s = Math.min(W / w, H / h)
	return rectQuad((W - w * s) / 2, (H - h * s) / 2, w * s, h * s)
}

/** The server frame fitted and centred in the output: where a new slice lands. */
export const frameInOutput = ({ width, height }) => fitQuad(WIDTH, HEIGHT, width, height)

export const rectPath = (x, y, w, h) => `M${x} ${y}L${x + w} ${y}L${x + w} ${y + h}L${x} ${y + h}Z`

export const newSlice = (output, name, color = 0) => ({ name, color, input: FULL_FRAME, output: frameInOutput(output) })

export const newSurface = (name, path, slice = '', fit = DEFAULT_FIT) => ({
	name,
	path,
	role: 'lit',
	feather: 0,
	offset: 0,
	slice,
	fit,
})

const parseFit = f => {
	if (f?.mode !== 'own') return DEFAULT_FIT
	return isQuad(f.quad) ? { mode: 'own', quad: f.quad } : OWN_FIT
}

/** A new map: one slice taking the whole frame, one surface covering the whole output on it. */
export const newMapData = (output = DEFAULT_OUTPUT) => {
	const slice = newId('sl')
	return {
		output: { ...output },
		pin: null,
		slices: { [slice]: newSlice(output, 'Slice 1') },
		surfaces: { [newId('sf')]: newSurface('Surface 1', rectPath(0, 0, output.width, output.height), slice) },
	}
}

/** `{ output: { width, height }, pin, slices: { <id>: { name, color, input, output, owner?, from? } }, surfaces: { <id>: { name, path, role, feather, offset, slice, fit } } }`, whatever orc returned. A slice's `output` is where its windows see it; `color` indexes the slice colours. */
export const parseMapData = raw => {
	let v = raw
	if (typeof raw === 'string') {
		try {
			v = JSON.parse(raw)
		} catch {
			v = null
		}
	}
	const output = {
		width: size(v?.output?.width, DEFAULT_OUTPUT.width),
		height: size(v?.output?.height, DEFAULT_OUTPUT.height),
	}
	const slices = {}
	for (const [id, s] of Object.entries(isPlainObject(v?.slices) ? v.slices : {})) {
		if (!isQuad(s?.input) || !isQuad(s?.output)) continue
		slices[id] = {
			name: typeof s.name === 'string' ? s.name : id,
			color: Number.isInteger(s.color) && SLICE_COLORS[s.color] ? s.color : nextSliceColor(slices),
			input: s.input,
			output: s.output,
		}
		if (typeof s.owner === 'string' && s.owner)
			Object.assign(slices[id], { owner: s.owner, from: typeof s.from === 'string' ? s.from : '' })
	}
	const surfaces = {}
	for (const [id, s] of Object.entries(isPlainObject(v?.surfaces) ? v.surfaces : {})) {
		if (typeof s?.path !== 'string' || !s.path) continue
		surfaces[id] = {
			name: typeof s.name === 'string' ? s.name : id,
			path: s.path,
			role: s.role === 'cut' ? 'cut' : 'lit',
			feather: Math.max(0, num(s.feather, 0)),
			offset: num(s.offset, 0),
			slice: slices[s.slice] ? s.slice : '',
			fit: parseFit(s.fit),
			hidden: s.hidden === true,
		}
	}
	return { output, pin: isQuad(v?.pin) ? v.pin : null, slices, surfaces }
}

export const transformMap = map => (map ? { ...map, data: parseMapData(map.data) } : null)

const fromFrame = ([u, v]) => [STAGE_LEFT + u * WIDTH, STAGE_TOP + v * HEIGHT]
const toFrame = ([x, y]) => [(x - STAGE_LEFT) / WIDTH, (y - STAGE_TOP) / HEIGHT]

/** Output px → stage for a surface: back through its picture, the frame being the stage; any other surface's output is fitted and centred in the stage. */
export const outputToStage = (data, surface) => {
	const m = surfaceToFrame(data, surface)
	if (m) return p => fromFrame(applyHomography(m, p))
	const { width: W, height: H } = data.output
	const s = Math.min(WIDTH / W, HEIGHT / H)
	return ([x, y]) => [(x - W / 2) * s, (y - H / 2) * s]
}

/** The inverse of `outputToStage`. */
export const stageToOutput = (data, surface) => {
	const q = surfaceQuad(data, surface)
	const m = q && quadToQuad(data.slices[surface.slice].input, q)
	if (m) return p => applyHomography(m, toFrame(p))
	const { width: W, height: H } = data.output
	const s = Math.min(WIDTH / W, HEIGHT / H)
	return ([x, y]) => [x / s + W / 2, y / s + H / 2]
}

/** `d` with every anchor and handle through `fn`. A projective `fn` bends curves only approximately. */
export const mapPath = (d, fn) => pathString(mapNodes(parsePath(d), fn))

/** `data` at a new output size: slice outputs, surfaces, feather and offset scale with it. `keep` scales both axes alike, centred, so shapes keep their proportions. */
export const resizeOutput = (data, width, height, keep = false) => {
	const { width: W, height: H } = data.output
	const k0 = Math.min(width / W, height / H)
	const sx = keep ? k0 : width / W
	const sy = keep ? k0 : height / H
	const ox = keep ? (width - W * k0) / 2 : 0
	const oy = keep ? (height - H * k0) / 2 : 0
	const k = Math.sqrt(sx * sy)
	const fn = ([x, y]) => [x * sx + ox, y * sy + oy]
	return {
		...data,
		output: { width, height },
		slices: Object.fromEntries(
			Object.entries(data.slices).map(([id, s]) => [id, { ...s, output: s.output.map(fn) }])
		),
		surfaces: Object.fromEntries(
			Object.entries(data.surfaces).map(([id, s]) => [
				id,
				{
					...s,
					path: mapPath(s.path, fn),
					feather: s.feather * k,
					offset: s.offset * k,
					...(s.fit?.quad && { fit: { ...s.fit, quad: s.fit.quad.map(fn) } }),
				},
			])
		),
	}
}

/** The next free "<base> n" name among `items`. */
export const nextName = (items, base) => {
	const taken = new Set(Object.values(items).map(i => i.name))
	let n = Object.keys(items).length + 1
	while (taken.has(`${base} ${n}`)) n++
	return `${base} ${n}`
}
